import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, NavLink, Link } from "react-router-dom";
import "../styles/layout.css";
import NotificationBell from "./NotificationBell";
import { socket } from "../socket";
import { getAssetUrl } from "../utils/url";
import API from "../api";

function buildProfileMeta(user) {
  const role = user?.role || "patient";

  const requiredByRole = {
    patient: ["name", "email", "phone", "gender", "dateOfBirth"],
    doctor: [
      "name",
      "email",
      "specialization",
      "education",
      "experience",
      "clinic",
      "city",
      "fee",
      "gender",
    ],
    pharmacy: [
      "name",
      "email",
      "pharmacyName",
      "ownerName",
      "phone",
      "city",
      "address",
      "licenseNumber",
    ],
    insurer: ["name", "email", "companyName", "phone"],
    admin: ["name", "email"],
  };

  const destinationByRole = {
    patient: "/dashboard/profile",
    doctor: "/dashboard/doctor/profile",
    pharmacy: "/dashboard/pharmacy/profile",
    insurer: "/dashboard/insurer/profile",
    admin: "/dashboard/admin/profile",
  };

  const required = requiredByRole[role] || ["name", "email"];

  const filledCount = required.filter((key) => {
    const value = user?.[key];
    if (value === null || value === undefined) return false;
    if (typeof value === "number") return String(value) !== "";
    return String(value).trim() !== "";
  }).length;

  const percent =
    user?.profileCompletion?.percent ??
    Math.round((filledCount / required.length) * 100);

  return {
    role,
    percent,
    complete: percent >= 100 || user?.profileCompleted === true,
    destination: destinationByRole[role] || "/dashboard",
  };
}

function getRoleLabel(role = "patient") {
  const map = {
    patient: "Patient",
    doctor: "Doctor",
    admin: "Administrator",
    pharmacy: "Pharmacy",
    insurer: "Insurer",
  };
  return map[role] || "User";
}

function getNavigationMenu(role) {
  const commonForPatients = [
    { to: "/dashboard", label: "Dashboard", icon: "fa-solid fa-house" },
    { to: "/dashboard/doctors", label: "Find Doctors", icon: "fa-solid fa-user-doctor" },
    { to: "/dashboard/ehr", label: "My EHR", icon: "fa-solid fa-file-medical" },
    { to: "/dashboard/appointments", label: "Appointments", icon: "fa-solid fa-calendar-check" },
    { to: "/dashboard/messages", label: "Messages", icon: "fa-solid fa-comments" },
    { to: "/dashboard/bills", label: "Bills", icon: "fa-solid fa-file-invoice-dollar" },
    { to: "/dashboard/claims", label: "Claims", icon: "fa-solid fa-shield-heart" },
    { to: "/dashboard/prescriptions", label: "Prescriptions", icon: "fa-solid fa-capsules" },
  ];

  const menuForDoctors = [
    { to: "/dashboard", label: "Dashboard", icon: "fa-solid fa-house" },
    { to: "/dashboard/doctor/appointments", label: "Appointments", icon: "fa-solid fa-calendar-days" },
    { to: "/dashboard/patients", label: "Patients", icon: "fa-solid fa-users" },
    { to: "/dashboard/messages", label: "Messages", icon: "fa-solid fa-comments" },
    { to: "/dashboard/doctor/billing", label: "Billing", icon: "fa-solid fa-receipt" },
  ];

  const menuForAdmin = [
    { to: "/dashboard/admin", label: "Dashboard", icon: "fa-solid fa-shield-halved" },
    { to: "/dashboard/admin/verifications", label: "Verifications", icon: "fa-solid fa-user-check" },
    { to: "/dashboard/admin/audit-logs", label: "Audit Logs", icon: "fa-solid fa-clipboard-list" },
    { to: "/dashboard/admin/users", label: "Users", icon: "fa-solid fa-users-gear" },
    { to: "/dashboard/admin/appointments", label: "Appts", icon: "fa-solid fa-calendar-days" },
    { to: "/dashboard/admin/announcements", label: "Announce", icon: "fa-solid fa-bullhorn" },
  ];

  const menuForPharmacy = [
    { to: "/dashboard/pharmacy", label: "Dashboard", icon: "fa-solid fa-house" },
    { to: "/dashboard/pharmacy/prescriptions", label: "Prescription Queue", icon: "fa-solid fa-receipt" },
    { to: "/dashboard/pharmacy/inventory", label: "Inventory", icon: "fa-solid fa-capsules" },
  ];

  const menuForInsurer = [
    { to: "/dashboard", label: "Dashboard", icon: "fa-solid fa-house" },
    { to: "/dashboard/insurer/claims", label: "Claims", icon: "fa-solid fa-shield-heart" },
    { to: "/dashboard/insurer/members", label: "Members", icon: "fa-solid fa-id-card" },
  ];

  if (role === "admin") return menuForAdmin;
  if (role === "doctor") return menuForDoctors;
  if (role === "pharmacy") return menuForPharmacy;
  if (role === "insurer") return menuForInsurer;
  return commonForPatients;
}

export default function Topbar({
  user = {},
  setUser,
}) {
  const navigate = useNavigate();
  const profileMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Fall back to initials if the avatar image fails to load (e.g. an external
  // profile-pic URL is slow/blocked) so the corner never shows a broken image.
  const [avatarBroken, setAvatarBroken] = useState(false);
  useEffect(() => setAvatarBroken(false), [user?.profilePic]);

  const role = user?.role || "patient";
  const navMenu = useMemo(() => getNavigationMenu(role), [role]);

  const initials = useMemo(() => {
    const n = user?.name?.trim() || user?.pharmacyName?.trim();
    if (!n) return "U";
    return n
      .split(" ")
      .slice(0, 2)
      .map((x) => x[0])
      .join("")
      .toUpperCase();
  }, [user]);

  const profileMeta = useMemo(() => buildProfileMeta(user), [user]);

  const goToProfile = () => {
    setMenuOpen(false);
    navigate(profileMeta.destination);
  };

  const handleLogout = async () => {
    try {
      socket.disconnect();
    } catch (e) {}

    try { await API.post("/auth/logout"); } catch (e) { /* clear locally regardless */ }

    localStorage.removeItem("user");
    setUser?.(null);
    setMenuOpen(false);
    navigate("/");
  };

  useEffect(() => {
    const handleOutside = (e) => {
      if (!profileMenuRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
      if (!mobileMenuRef.current?.contains(e.target) && !e.target.closest('.mobile-hamburger')) {
        setMobileMenuOpen(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  return (
    <div className="topbar-wrapper">
      {/* LEFT: LOGO (Outside the Pill) */}
      <div className="topbar-logo-side">
        <Link to="/" className="logo" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <i className="fa-solid fa-notes-medical" style={{ marginRight: '8px', fontSize: '1.6rem', color: '#7c3aed' }}></i>
          <span style={{ color: '#4c1d95', fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.02em', fontFamily: 'var(--font-display)' }}>
            MediSure <span style={{ color: '#7c3aed', fontSize: '1.1rem', fontWeight: 700, verticalAlign: 'top' }}>HEALTH</span>
          </span>
        </Link>
      </div>

      {/* THE PILL: NAV ONLY */}
      <header className="topbar-pill">
        <nav className="topbar-nav-desktop">
            <ul className="topbar-nav-links">
               {navMenu.map(link => (
                 <li key={link.to}>
                   <NavLink 
                     to={link.to} 
                     className={({ isActive }) => isActive ? 'active' : ''}
                     end={link.to === "/dashboard"}
                   >
                     <i className={link.icon} style={{ fontSize: '0.9rem', opacity: 0.8 }} />
                     <span>{link.label}</span>
                   </NavLink>
                 </li>
               ))}
            </ul>
        </nav>
      </header>

      {/* RIGHT: UTILS + PROFILE (Now Outside the Pill) */}
      <div className="topbar-right">
        <NotificationBell />

        <div className="topbar-profile-wrap" ref={profileMenuRef}>
          <button
            type="button"
            className="topbar-profile"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Account menu"
            aria-expanded={menuOpen}
            style={{ gap: 10 }}
          >
            <div className="avatar" style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--brand-600)', color: '#fff', fontSize: '1rem', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {user?.profilePic && !avatarBroken ? (
                <img
                  src={getAssetUrl(user.profilePic)}
                  alt={`${user.name || "Your"} avatar`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={() => setAvatarBroken(true)}
                />
              ) : initials}
            </div>
            <i className="fa-solid fa-chevron-down profile-caret" style={{ marginLeft: 6, fontSize: '0.75rem' }} />
          </button>

          <AnimatePresence>
            {menuOpen ? (
              <motion.div
                className="topbar-profile-menu"
                initial={{ opacity: 0, y: 8, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.985 }}
                transition={{ duration: 0.18 }}
              >
                <div className="topbar-profile-menu-head">
                  <div className="topbar-profile-menu-avatar" style={{ background: 'var(--brand-500)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {user?.profilePic && !avatarBroken ? (
                      <img
                        src={getAssetUrl(user.profilePic)}
                        alt={`${user.name || "Your"} avatar`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setAvatarBroken(true)}
                      />
                    ) : initials}
                  </div>
                  <div className="topbar-profile-menu-copy">
                    <div className="topbar-profile-menu-name">{user?.name || "User"}</div>
                    <div className="topbar-profile-menu-email">{getRoleLabel(role)}</div>
                  </div>
                </div>

                {!profileMeta.complete && (
                  <button type="button" onClick={goToProfile} className="topbar-menu-btn" style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.2)', marginBottom: 8 }}>
                    <i className="fa-solid fa-circle-exclamation" style={{ color: '#f59e0b' }} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                       <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#b45309' }}>Finish Profile</span>
                       <span style={{ fontSize: '0.75rem', color: '#d97706' }}>{profileMeta.percent}% Complete</span>
                    </div>
                  </button>
                )}

                <button type="button" onClick={goToProfile} className="topbar-menu-btn">
                  <i className="fa-regular fa-user" />
                  <span>Profile Settings</span>
                </button>

                <button type="button" onClick={handleLogout} className="topbar-menu-btn danger">
                  <i className="fa-solid fa-right-from-bracket" />
                  <span>Logout</span>
                </button>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        {/* MOBILE HAMBURGER */}
        <button
          className="icon-btn mobile-hamburger mobile-only"
          onClick={() => setMobileMenuOpen(prev => !prev)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
          style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.1)', width: 40, height: 40, borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
        >
           <i className={mobileMenuOpen ? "fa-solid fa-xmark" : "fa-solid fa-bars"} style={{ color: '#4c1d95' }}></i>
        </button>
      </div>

      {/* MOBILE MENU DROPDOWN */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            style={{
              position: 'absolute',
              top: '120px',
              left: 10,
              right: 10,
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(139, 92, 246, 0.15)',
              borderRadius: 24,
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              zIndex: 999,
              boxShadow: '0 24px 48px rgba(0,0,0,0.12)'
            }}
          >
             {navMenu.map(link => (
               <NavLink 
                 key={link.to} 
                 to={link.to} 
                 onClick={() => setMobileMenuOpen(false)}
                 style={({ isActive }) => ({
                    padding: '14px 20px',
                    borderRadius: 16,
                    color: isActive ? '#4c1d95' : '#4b5563',
                    background: isActive ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                    textDecoration: 'none',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                 })}
                 end={link.to === "/dashboard"}
               >
                 <i className={link.icon} style={{ fontSize: '1rem', width: 20, color: '#7c3aed' }}></i>
                 {link.label}
               </NavLink>
             ))}
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
